import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { dayjs } from "~/helpers/date";

import {
  Container,
  LeftContainer,
  RightContainer,
  MainTextContainer,
  ShopifyLogo,
  TimerTextContainer,
  TimerText,
  CountdownContainer,
  StyledButton,
  StyledLink,
  MarcChaponImage,
  QuoteText,
  QuoteTextHighlight,
  QuoteAuthor,
  ValueContainer,
  CentsStoreText
} from "./styles";

import { SHOW_STORE_CREATION_MODAL } from "~/helpers/customEvents";
import theme from "~/constants/theme";
import { Emoji } from "~/components/ui/Emoji";
import CountdownTimer from "~/components/ui/Countdown";
import Modal from "~/components/ui/Modal";
import { useModalTrigger } from "~/hooks/useModalTrigger";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { usePlans } from "~/api/billing/queries";
// @ts-ignore
import { ReactComponent as LoaderSVG } from "~/assets/svg/loader.svg";
import {
  separateCentsFromNumberWithZero
} from "~/helpers/numbers";
import { IPlan, SubscriptionType } from "~/types/billing";



type StoreCreationProps = {
  onContinue: (shouldCreateStore: boolean) => Promise<void>;
};

export const StoreCreation = ({ onContinue }: StoreCreationProps) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("tablet");
  const [isModalOpen] = useModalTrigger(SHOW_STORE_CREATION_MODAL, true);
  
  const [isTheCountdownOver, setIsTheCountdownOver] = useState(false);
  const { data: plans, isLoading } = usePlans();

  useEffect(() => {
    localStorage.removeItem("onboarding_with_create_store");
    localStorage.removeItem("customizationStepIndex");
    localStorage.removeItem("onboardingStepIndex");
  }, []);

  const currentTime = new Date().getTime();
  const formattedTime = dayjs(currentTime)
    .add(1, "m")
    .tz("America/Los_Angeles")
    .format();

  const timerTime = new Date(formattedTime);

  const basedPlan = plans?.filter(
    (plan) => plan.interval === SubscriptionType.MONTHLY && (plan?.store_creation_tax_cents ?? 0)
  )[0]

  const storeTaxValue = separateCentsFromNumberWithZero(basedPlan?.store_creation_tax_cents || 100);

  return (
    <Modal
      id="create-store-modal"
      isShowing={isModalOpen}
      padding={isDesktop ? "24px" : "24px 12px"}
      minWidth="min(90%, 482px)"
      overflow="hidden">
      <Container data-testid="onboarding-create-store-modal">
        {isLoading ? (
          <>
            <LoaderSVG />
          </>
        ) : (
          <>
            <LeftContainer>
              <ShopifyLogo />
              <MainTextContainer>
                <>
                  <Trans
                    i18nKey="store-creation-modal.mainText.userWithoutStore.value"
                    components={{
                      1: <ValueContainer translate="no" />,
                      2: <CentsStoreText translate="no" />,
                    }}
                    values={{
                      valueFull: storeTaxValue[0],
                      valueCents: storeTaxValue[1],
                    }}
                  />
                  <Emoji label="Rocket Emoji" symbol="🚀" />
                </>
              </MainTextContainer>
              {!isTheCountdownOver ? (
                <TimerTextContainer>
                  <TimerText>{t("store-creation-modal.timerText")}</TimerText>
                  <CountdownContainer>
                    <CountdownTimer
                      callback={() => setIsTheCountdownOver(true)}
                      targetDate={timerTime}
                      numberColor={theme.colors.primary}
                      bgColor="transparent"
                      dots
                      dotsColor={theme.colors.primary}
                      dotsHeight={1.4}
                      gap={0}
                    />
                  </CountdownContainer>
                </TimerTextContainer>
              ) : null}
              <StyledButton
                onClick={() => onContinue(true)}
                title={t("store-creation-modal.button.success.title") as string}
                variant="default">
                {t("store-creation-modal.button.success.label")}
              </StyledButton>
              <StyledLink
                title={t("store-creation-modal.button.reject.title") as string}
                data-heap="onboarding-new-shopify-store-modal-reject-link"
                variant="default"
                onClick={() => onContinue(false)}>
                {t("store-creation-modal.button.reject.label") as string}
              </StyledLink>
            </LeftContainer>
            <RightContainer>
              <MarcChaponImage />
              <QuoteText>
                <Trans
                  i18nKey="store-creation-modal.quoteText"
                  components={{
                    1: <QuoteTextHighlight />,
                  }}
                />
              </QuoteText>
              <QuoteAuthor>- Kyle Borawaski, Entrepreneur</QuoteAuthor>
            </RightContainer>
          </>
        )}
      </Container>
    </Modal>
  );
};
